import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ImageUpload } from "../components/ImageUpload";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, Star, Loader2 } from "lucide-react";

const Field = ({ label, value, onChange, testid, textarea }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-brand-muted">{label}</label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        rows={2}
        className="mt-2 w-full rounded-xl bg-brand-surface border border-brand-line px-4 py-2.5 text-brand-text outline-none focus:border-brand-gold transition-colors resize-none"
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full rounded-xl bg-brand-surface border border-brand-line px-4 py-2.5 text-brand-text outline-none focus:border-brand-gold transition-colors"
      />
    )}
  </div>
);

const emptyItem = {
  category: "Mexican Dishes", name_en: "", name_es: "", description_en: "", description_es: "",
  price: "", image_url: "", featured: false, order: 99,
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("content");
  const [settings, setSettings] = useState(null);
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/content").then((res) => {
    setSettings(res.data.settings);
    setMenu(res.data.menu);
    setCategories(res.data.categories);
  });

  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/"); };

  const setS = (path, val) => {
    setSettings((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...(obj[parts[i]] || {}) };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { data: settings });
      toast.success("Content saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveItem = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.is_deleted;
      if (editing.id) await api.put(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Menu item saved");
      setEditing(null);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/menu/${id}`);
    toast.success("Deleted");
    load();
  };

  if (!settings) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="animate-spin text-brand-primary" /></div>;
  }

  const catOptions = ["Mexican Dishes", "Appetizers", "Salad", "Sides", "Desserts", "Drinks", ...categories.filter((c) => !["Mexican Dishes", "Appetizers", "Salad", "Sides", "Desserts", "Drinks"].includes(c))];

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text" data-testid="admin-page">
      <header className="sticky top-0 z-40 glass border-b border-brand-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-lg bg-white px-2 py-1"><img src={resolveImg(settings.logo_url)} alt="" className="h-7 w-auto max-w-[110px] object-contain" /></span>
            <span className="font-display text-lg">Admin · {settings.business_name}</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-brand-muted hover:text-brand-text transition-colors">View site ↗</a>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-lg border border-brand-line hover:border-brand-primary px-4 py-2 text-sm transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-3 mb-10">
          {["content", "menu"].map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              data-testid={`tab-${tb}`}
              className={`rounded-full px-6 py-2.5 text-sm font-medium capitalize transition-colors ${tab === tb ? "bg-brand-gold text-brand-bg" : "border border-brand-line text-brand-muted hover:text-brand-text"}`}
            >
              {tb === "content" ? "Site Content" : "Menu Items"}
            </button>
          ))}
        </div>

        {tab === "content" && (
          <div className="space-y-8" data-testid="content-editor">
            <section className="rounded-2xl border border-brand-line p-6">
              <h2 className="font-display text-2xl mb-6">Branding & Hero</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Business Name" value={settings.business_name} onChange={(v) => setS("business_name", v)} testid="f-business-name" />
                <Field label="DoorDash / Order URL" value={settings.doordash_url} onChange={(v) => setS("doordash_url", v)} testid="f-doordash" />
                <ImageUpload label="Logo" value={settings.logo_url} onChange={(v) => setS("logo_url", v)} testid="upload-logo" />
                <ImageUpload label="Hero Background" value={settings.hero_bg} onChange={(v) => setS("hero_bg", v)} testid="upload-hero" />
                <Field label="Hero Kicker (EN)" value={settings.hero_kicker?.en} onChange={(v) => setS("hero_kicker.en", v)} testid="f-kicker-en" />
                <Field label="Hero Kicker (ES)" value={settings.hero_kicker?.es} onChange={(v) => setS("hero_kicker.es", v)} testid="f-kicker-es" />
                <Field label="Tagline (EN)" value={settings.tagline?.en} onChange={(v) => setS("tagline.en", v)} testid="f-tagline-en" />
                <Field label="Tagline (ES)" value={settings.tagline?.es} onChange={(v) => setS("tagline.es", v)} testid="f-tagline-es" />
                <Field label="Hero Subtitle (EN)" value={settings.hero_subtitle?.en} onChange={(v) => setS("hero_subtitle.en", v)} testid="f-sub-en" textarea />
                <Field label="Hero Subtitle (ES)" value={settings.hero_subtitle?.es} onChange={(v) => setS("hero_subtitle.es", v)} testid="f-sub-es" textarea />
              </div>
            </section>

            <section className="rounded-2xl border border-brand-line p-6">
              <h2 className="font-display text-2xl mb-6">Visit Info</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Address" value={settings.address} onChange={(v) => setS("address", v)} testid="f-address" />
                <Field label="Phone" value={settings.phone} onChange={(v) => setS("phone", v)} testid="f-phone" />
                <Field label="Email" value={settings.email} onChange={(v) => setS("email", v)} testid="f-email" />
                <Field label="Hours (EN)" value={settings.hours?.en} onChange={(v) => setS("hours.en", v)} testid="f-hours-en" />
                <Field label="Hours (ES)" value={settings.hours?.es} onChange={(v) => setS("hours.es", v)} testid="f-hours-es" />
              </div>
            </section>

            <button onClick={saveSettings} disabled={saving} data-testid="save-content-btn" className="inline-flex items-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primaryHover px-8 py-3.5 font-semibold transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Content
            </button>
          </div>
        )}

        {tab === "menu" && (
          <div data-testid="menu-editor">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">Menu Items ({menu.length})</h2>
              <button onClick={() => setEditing({ ...emptyItem })} data-testid="add-item-btn" className="inline-flex items-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primaryHover px-5 py-2.5 text-sm font-semibold transition-colors">
                <Plus size={16} /> Add Item
              </button>
            </div>

            <div className="grid gap-3">
              {menu.map((item) => (
                <div key={item.id} className="flex items-center gap-4 rounded-xl border border-brand-line p-3" data-testid={`admin-item-${item.id}`}>
                  {item.image_url ? <img src={resolveImg(item.image_url)} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="h-14 w-14 rounded-lg bg-brand-surface" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium flex items-center gap-2">{item.name_en} {item.featured && <Star size={14} className="text-brand-gold fill-brand-gold" />}</p>
                    <p className="text-sm text-brand-muted">{item.category} · {item.price}</p>
                  </div>
                  <button onClick={() => setEditing(item)} data-testid={`edit-item-${item.id}`} className="rounded-lg border border-brand-line hover:border-brand-gold px-4 py-2 text-sm transition-colors">Edit</button>
                  <button onClick={() => del(item.id)} data-testid={`delete-item-${item.id}`} className="rounded-lg border border-brand-line hover:border-brand-primary p-2 text-brand-primary transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="item-modal" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-brand-surface border border-brand-line p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-6">{editing.id ? "Edit Item" : "New Item"}</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-brand-muted">Category</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="item-category" className="mt-2 w-full rounded-xl bg-brand-bg border border-brand-line px-4 py-2.5 outline-none focus:border-brand-gold">
                  {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Field label="Price" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="item-price" />
              <Field label="Name (EN)" value={editing.name_en} onChange={(v) => setEditing({ ...editing, name_en: v })} testid="item-name-en" />
              <Field label="Name (ES)" value={editing.name_es} onChange={(v) => setEditing({ ...editing, name_es: v })} testid="item-name-es" />
              <Field label="Description (EN)" value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} testid="item-desc-en" textarea />
              <Field label="Description (ES)" value={editing.description_es} onChange={(v) => setEditing({ ...editing, description_es: v })} testid="item-desc-es" textarea />
              <ImageUpload label="Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testid="item-upload" />
              <label className="flex items-center gap-3 mt-6">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="item-featured" className="h-5 w-5 accent-brand-gold" />
                <span className="text-sm">Show in Featured section</span>
              </label>
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={saveItem} disabled={saving} data-testid="save-item-btn" className="inline-flex items-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primaryHover px-6 py-3 font-semibold transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
              </button>
              <button onClick={() => setEditing(null)} className="rounded-xl border border-brand-line px-6 py-3 transition-colors hover:border-brand-muted">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
